package diana.soleil.design.iraniancanadienbazzar.core.data.fake

import diana.soleil.design.iraniancanadienbazzar.core.model.Address
import diana.soleil.design.iraniancanadienbazzar.core.model.Business
import diana.soleil.design.iraniancanadienbazzar.core.model.BusinessStatus
import diana.soleil.design.iraniancanadienbazzar.core.model.Category
import diana.soleil.design.iraniancanadienbazzar.core.model.LocalizedText
import diana.soleil.design.iraniancanadienbazzar.core.model.Sponsorship

/**
 * Small interim dataset for the in-memory repositories — enough to populate the screens (across all
 * three launch metros, with a couple localized fields) until the GitLive Firestore data source lands.
 * Domain values only; not localizable UI copy. Replaced by real Firestore reads, not shipped.
 */
internal object FakeData {

    val categories: List<Category> = listOf(
        Category(
            "food_restaurants", "Food & Restaurants", "restaurant", 26, 0,
            LocalizedText(en = "Food & Restaurants", fr = "Restaurants et alimentation", fa = "خوراک و رستوران‌ها"),
        ),
        Category(
            "medical_health", "Medical & Health", "medical_services", 27, 1,
            LocalizedText(en = "Medical & Health", fr = "Santé et médecine", fa = "پزشکی و سلامت"),
        ),
        Category(
            "beauty_wellness", "Beauty & Wellness", "spa", 34, 2,
            LocalizedText(en = "Beauty & Wellness", fr = "Beauté et bien-être", fa = "زیبایی و تندرستی"),
        ),
        Category(
            "legal_services", "Legal Services", "gavel", 29, 3,
            LocalizedText(en = "Legal Services", fr = "Services juridiques", fa = "خدمات حقوقی"),
        ),
        Category(
            "finance_accounting", "Finance & Accounting", "account_balance", 10, 4,
            LocalizedText(en = "Finance & Accounting", fr = "Finances et comptabilité", fa = "مالی و حسابداری"),
        ),
        Category(
            "real_estate", "Real Estate", "home_work", 12, 5,
            LocalizedText(en = "Real Estate", fr = "Immobilier", fa = "املاک"),
        ),
        Category(
            "grocery_specialty_food", "Grocery & Specialty Food", "shopping_cart", 7, 6,
            LocalizedText(en = "Grocery & Specialty Food", fr = "Épicerie et produits spécialisés", fa = "خواربار و مواد غذایی ویژه"),
        ),
        Category(
            "education_tutors", "Education & Tutors", "school", 5, 7,
            LocalizedText(en = "Education & Tutors", fr = "Éducation et tutorat", fa = "آموزش و تدریس خصوصی"),
        ),
        Category(
            "community_services", "Community Services", "diversity_3", 7, 8,
            LocalizedText(en = "Community Services", fr = "Services communautaires", fa = "خدمات اجتماعی"),
        ),
        Category(
            "shopping", "Shopping", "shopping_bag", 5, 9,
            LocalizedText(en = "Shopping", fr = "Magasinage", fa = "خرید"),
        ),
        Category(
            "events_entertainment", "Events & Entertainment", "celebration", 1, 10,
            LocalizedText(en = "Events & Entertainment", fr = "Événements et divertissement", fa = "رویدادها و سرگرمی"),
        ),
    )

    val businesses: List<Business> = listOf(
        business(
            id = "fake-shiraz-palace", name = "Shiraz Palace", category = "food_restaurants",
            serviceTitle = LocalizedText(en = "Persian Restaurant", fr = "Restaurant perse", fa = "رستوران ایرانی"),
            description = LocalizedText(
                en = "Authentic Persian cuisine — saffron rice, charcoal kababs, warm hospitality.",
                fa = "غذای اصیل ایرانی؛ چلوکباب و برنج زعفرانی.",
            ),
            city = "Richmond Hill", province = "ON", rating = 4.8, reviews = 132, sponsored = true, verified = true,
            price = 2,
            coverUrl = "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=1000&q=70",
            photos = listOf("https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=1000&q=70", "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=1000&q=70", "https://images.unsplash.com/photo-1600891964092-4316c288032e?auto=format&fit=crop&w=1000&q=70"),
        ),
        business(
            id = "fake-tehran-grill", name = "Tehran Grill", category = "food_restaurants",
            serviceTitle = LocalizedText(en = "Kababs & More"),
            description = LocalizedText(en = "Family-run kitchen serving home-style Persian classics and fresh sangak."),
            city = "Toronto", province = "ON", rating = 4.6, reviews = 88, sponsored = false, verified = true,
            price = 1,
            coverUrl = "https://images.unsplash.com/photo-1555939594-58d7cb561ad1?auto=format&fit=crop&w=1000&q=70",
            photos = listOf("https://images.unsplash.com/photo-1555939594-58d7cb561ad1?auto=format&fit=crop&w=1000&q=70", "https://images.unsplash.com/photo-1529193591184-b1d58069ecdd?auto=format&fit=crop&w=1000&q=70", "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=1000&q=70"),
        ),
        business(
            id = "fake-pars-market", name = "Pars Market", category = "grocery_specialty_food",
            serviceTitle = LocalizedText(en = "Persian Grocery"),
            description = LocalizedText(en = "Premium Persian groceries, fresh herbs, sweets, and pantry staples."),
            city = "North York", province = "ON", rating = 4.7, reviews = 54, sponsored = true, verified = false,
            price = 2,
        ),
        business(
            id = "fake-azadi-law", name = "Azadi Law", category = "legal_services",
            serviceTitle = LocalizedText(en = "Immigration Lawyer", fr = "Avocat en immigration"),
            description = LocalizedText(en = "Immigration and family law, fluent in Farsi, English, and French."),
            city = "Montréal", province = "QC", rating = 5.0, reviews = 41, sponsored = false, verified = true,
            price = 3,
        ),
        business(
            id = "fake-caspian-dental", name = "Caspian Dental", category = "medical_health",
            serviceTitle = LocalizedText(en = "Dental Clinic"),
            description = LocalizedText(en = "Comprehensive family and cosmetic dentistry."),
            city = "North Vancouver", province = "BC", rating = 4.9, reviews = 76, sponsored = true, verified = true,
            price = 3,
            coverUrl = "https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?auto=format&fit=crop&w=1000&q=70",
            photos = listOf("https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?auto=format&fit=crop&w=1000&q=70", "https://images.unsplash.com/photo-1606811841689-23dfddce3e95?auto=format&fit=crop&w=1000&q=70", "https://images.unsplash.com/photo-1600891964092-4316c288032e?auto=format&fit=crop&w=1000&q=70"),
        ),
        business(
            id = "fake-shahrzad-salon", name = "Shahrzad Beauty", category = "beauty_wellness",
            serviceTitle = LocalizedText(en = "Beauty Salon", fa = "آرایشگاه"),
            description = LocalizedText(en = "Hair, skin, and bridal services."),
            city = "West Vancouver", province = "BC", rating = 4.8, reviews = 63, sponsored = false, verified = false,
            price = 2,
        ),
    )

    private fun business(
        id: String,
        name: String,
        category: String,
        serviceTitle: LocalizedText,
        description: LocalizedText,
        city: String,
        province: String,
        rating: Double,
        reviews: Int,
        sponsored: Boolean,
        verified: Boolean,
        price: Int? = null,
        // Real cover + gallery photos for a few demo businesses so the full-screen photo viewer
        // and photo cards are demonstrable (mirrors the dev-Firestore seed for dev-* businesses).
        coverUrl: String? = null,
        photos: List<String> = emptyList(),
        languages: List<String> = listOf("Farsi", "English"),
    ): Business = Business(
        id = id,
        name = name,
        serviceTitle = serviceTitle,
        categoryId = category,
        description = description,
        status = BusinessStatus.Approved,
        verified = verified,
        phone = "+14165550100",
        address = Address(line = "100 Sample St", city = city, province = province),
        metroId = diana.soleil.design.iraniancanadienbazzar.core.model.Metros.forCity(city)?.id,
        priceLevel = price,
        rating = rating,
        reviewCount = reviews,
        coverUrl = coverUrl,
        photos = photos,
        mediaStatus = if (coverUrl != null) diana.soleil.design.iraniancanadienbazzar.core.model.MediaStatus.OwnerMedia
            else diana.soleil.design.iraniancanadienbazzar.core.model.MediaStatus.CategoryPlaceholderPending,
        languagesSpoken = languages,
        sponsorship = Sponsorship(active = sponsored, plan = if (sponsored) "sponsored" else "free"),
    )
}
